<template>
  <div class="settings-page">
    <h2>Preferences</h2>
    <div class="subtitle">Customize your Routine experience.</div>

    <section>
      <div class="entry">
        <label>Theme</label>
        <BaseButtonGroup>
          <template #firstButton>
            <BaseButton
              kind="main"
              type="secondary"
              text="Light"
              :state="themePreference === 'light' ? 'active' : 'default'"
              @click="themePreference = 'light'"
            >
              <template #icon>
                <OnboardingLightIcon />
              </template>
            </BaseButton>
          </template>
          <template #middleButtons>
            <BaseButton
              kind="main"
              type="secondary"
              text="Dark"
              :state="themePreference === 'dark' ? 'active' : 'default'"
              @click="themePreference = 'dark'"
            >
              <template #icon>
                <OnboardingDarkIcon />
              </template>
            </BaseButton>
          </template>
          <template #lastButton>
            <BaseButton
              kind="main"
              type="secondary"
              text="System"
              :state="themePreference === 'system' ? 'active' : 'default'"
              @click="themePreference = 'system'"
            >
              <template #icon>
                <OnboardingDesktopIcon />
              </template>
            </BaseButton>
          </template>
        </BaseButtonGroup>
      </div>

      <div class="entry">
        <label>Layout direction</label>
        <BaseButtonGroup>
          <template #firstButton>
            <BaseButton
              kind="main"
              type="secondary"
              text="LTR"
              :state="directionPreference === 'ltr' ? 'active' : 'default'"
              @click="directionPreference = 'ltr'"
            >
              <template #icon>
                <OnboardingLtrIcon />
              </template>
            </BaseButton>
          </template>
          <template #middleButtons>
            <BaseButton
              kind="main"
              type="secondary"
              text="RTL"
              :state="directionPreference === 'rtl' ? 'active' : 'default'"
              @click="directionPreference = 'rtl'"
            >
              <template #icon>
                <OnboardingRtlIcon />
              </template>
            </BaseButton>
          </template>
          <template #lastButton>
            <BaseButton
              kind="main"
              type="secondary"
              text="System"
              :state="directionPreference === 'system' ? 'active' : 'default'"
              @click="directionPreference = 'system'"
            >
              <template #icon>
                <OnboardingDesktopIcon />
              </template>
            </BaseButton>
          </template>
        </BaseButtonGroup>
      </div>

      <div class="entry">
        <label>Time display</label>
        <div>
          <VSelect
            label="display"
            :clearable="false"
            :searchable="false"
            :reduce="(time: { id: number; display: string }) => time.id"
            :options="[
              { id: 24, display: '24H' },
              { id: 12, display: 'AM/PM' },
            ]"
            :model-value="is24hFormat ? 24 : 12"
            class="select"
            @update:model-value="(v: 24 | 12) => setTimeFormat(v)"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
          </VSelect>
        </div>
      </div>

      <div class="entry">
        <label>Working hours</label>
        <div class="actions">
          <VSelect
            label="label"
            :clearable="false"
            :searchable="false"
            :reduce="reduceTimeOption"
            :options="startTimeOptions"
            :model-value="selectedStartOption"
            class="select"
            @update:model-value="editStartWorkingHour"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
          </VSelect>
          <VSelect
            label="label"
            :clearable="false"
            :searchable="false"
            :reduce="reduceTimeOption"
            :options="endTimeOptions"
            :model-value="selectedEndOption"
            class="select"
            @update:model-value="editEndWorkingHour"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
          </VSelect>
        </div>
      </div>

      <div class="entry">
        <label>Starting day of the week</label>
        <div class="float-right">
          <VSelect
            v-model="weekStartOn"
            label="firstDay"
            :clearable="false"
            :searchable="false"
            :get-option-label="capitalize"
            :options="['sunday', 'monday']"
            class="select"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
          </VSelect>
        </div>
      </div>
      <div class="entry">
        <label>Show inbox counter</label>
        <BaseToggleButton :checked="inboxBadge" @change="toggleInboxBadge" />
      </div>
      <div class="entry">
        <label>Wide layout</label>
        <BaseToggleButton
          :checked="isWideDocument"
          @change="toggleWideDocument"
        />
      </div>
      <div v-if="in_electron" class="entry">
        <label>Launch at startup</label>
        <BaseToggleButton
          :checked="launchAtStartup"
          @change="toggleLaunchAtStartup"
        />
      </div>
      <div class="entry">
        <label>Spellcheck</label>
        <BaseToggleButton
          :checked="spellcheckEnabled"
          @change="toggleSpellcheck"
        />
      </div>
      <div v-if="areLanguagesSettable && spellcheckEnabled" class="entry">
        <label>Languages</label>
        <div class="languages-control">
          <VSelect
            :clearable="false"
            multiple
            :searchable="true"
            :options="availableSpellcheckLanguageOptions"
            :get-option-label="getLanguageOptionLabel"
            :reduce="reduceLanguageOption"
            :model-value="spellcheckLanguages"
            class="select"
            :disabled="!areLanguagesSettable"
            @update:model-value="setSpellcheckPreferredLanguages"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
          </VSelect>
          <div v-if="!areLanguagesSettable" class="note">
            On this platform, spellcheck languages are managed by the system
            settings.
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script lang="ts">
import { capitalize } from "lodash-es";
import { computed, defineComponent } from "vue";
import VSelect from "vue-select";

import type { Daytime } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseButtonGroup from "@/components/base/BaseButtonGroup.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import OnboardingDarkIcon from "@/components/icons/onboarding/DarkIcon.vue";
import OnboardingDesktopIcon from "@/components/icons/onboarding/DesktopIcon.vue";
import OnboardingLightIcon from "@/components/icons/onboarding/LightIcon.vue";
import OnboardingLtrIcon from "@/components/icons/onboarding/LtrIcon.vue";
import OnboardingRtlIcon from "@/components/icons/onboarding/RtlIcon.vue";
import * as inject from "@/components/inject";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useSpellcheck } from "@/composables/useSpellcheck";
import {
  useWorkingHours,
  type TimeOption,
} from "@/composables/useWorkingHours";
import { useReleaser } from "@/ref";
import { in_electron } from "@/utils/in";
import { Languages } from "@/utils/languages";

import { UseSettings } from "./controller/settings";

export default defineComponent({
  components: {
    OnboardingDarkIcon,
    OnboardingLightIcon,
    OnboardingDesktopIcon,
    OnboardingLtrIcon,
    OnboardingRtlIcon,
    BaseButton,
    BaseButtonGroup,
    BaseToggleButton,
    ChevronDownIcon,
    VSelect,
  },
  async setup() {
    const controller = inject.controller();
    const localeFns = inject.locale();
    const themePreference = inject.themePreference();
    const directionPreference = inject.directionPreference();
    const inboxBadge = inject.inboxBadge();
    const isWideDocument = inject.isWideDocument();
    const { is24hFormat, setTimeFormat, weekStartOn } =
      UseSettings.getInstance();
    const releaser = useReleaser();
    const hoursConf = await releaser.add(controller.settings.hours());

    const startWorkingHour = computed({
      get: () => hoursConf.value.day,
      set(day: Daytime) {
        controller.settings.patch({ hours: { day } });
      },
    });
    const endWorkingHour = computed({
      get: () => hoursConf.value.evening,
      set(evening: Daytime) {
        controller.settings.patch({ hours: { evening } });
      },
    });

    // Use working hours composable
    const {
      startTimeOptions,
      endTimeOptions,
      selectedStartOption,
      selectedEndOption,
      editStartWorkingHour,
      editEndWorkingHour,
    } = useWorkingHours(
      localeFns,
      startWorkingHour,
      endWorkingHour,
      is24hFormat
    );

    const toggleSpellcheck = (value: boolean) => {
      spellcheckEnabled.value = value;
      if (value) {
        enableSpellcheck();
      } else {
        disableSpellcheck();
      }
    };

    // Reduce function for v-select to extract Daytime value
    const reduceTimeOption = (time: TimeOption) => time.value;
    const toggleInboxBadge = () => {
      inboxBadge.value = !inboxBadge.value;
    };

    // Load & sync launch at startup with the browser's local storage.
    const { syncState } = useBrowserStore();
    const launchAtStartup = releaser.add(
      syncState<boolean>("startupLaunch", false)
    );

    const toggleLaunchAtStartup = (value: boolean) => {
      if (in_electron) {
        const { ipcRenderer } = require("electron");
        ipcRenderer.send("toggle-launch-at-startup", value);
        launchAtStartup.value = value;
      }
    };

    const {
      enabled: spellcheckEnabled,
      languages: spellcheckLanguages,
      listAvailableLanguages: listSpellcheckAvailableLanguages,
      enable: enableSpellcheck,
      disable: disableSpellcheck,
      setPreferredLanguages: setSpellcheckPreferredLanguages,
      areLanguagesSettable,
      release: releaseSpellcheck,
    } = useSpellcheck();
    releaser.defer(releaseSpellcheck);

    // Load available spellcheck languages (tag+label) for the selector
    const availableSpellcheckLanguageOptions =
      listSpellcheckAvailableLanguages();

    const getLanguageOptionLabel = (o: Languages.Language) => o.label;
    const reduceLanguageOption = (o: Languages.Language) => o.tag;

    // Wide document setting
    const toggleWideDocument = (value: boolean) => {
      isWideDocument.value = value;
    };

    // Set the initial value matching the local storage state.
    toggleLaunchAtStartup(launchAtStartup.value);

    return {
      capitalize,
      editEndWorkingHour,
      editStartWorkingHour,
      endTimeOptions,
      endWorkingHour,
      inboxBadge,
      is24hFormat,
      isWideDocument,
      reduceTimeOption,
      selectedEndOption,
      selectedStartOption,
      setTimeFormat,
      startTimeOptions,
      startWorkingHour,
      themePreference,
      directionPreference,
      toggleInboxBadge,
      toggleWideDocument,
      weekStartOn,
      in_electron,
      areLanguagesSettable,
      launchAtStartup,
      toggleLaunchAtStartup,
      // spellchecker
      spellcheckEnabled,
      spellcheckLanguages,
      availableSpellcheckLanguageOptions,
      listSpellcheckAvailableLanguages,
      enableSpellcheck,
      disableSpellcheck,
      setSpellcheckPreferredLanguages,
      toggleSpellcheck,
      getLanguageOptionLabel,
      reduceLanguageOption,
    };
  },
});
</script>
<style scoped lang="scss">
@use "../../styles/settings.scss";

.languages-control {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  :deep(.v-select.select) {
    min-width: 200px;
  }
}

.note {
  @extend %font-caption-regular;
  opacity: 0.75;
}

:deep(.v-select.select) {
  @extend %font-caption-regular;

  .vs__dropdown-toggle {
    height: 100%;
    padding: 0;

    .vs__selected {
      background-color: transparent;
      height: 100%;
      border: none;
      border-radius: 0;
      margin: 0;
    }

    .vs__search {
      padding: 0;
    }

    .vs__actions {
      padding-inline-end: 12px;
    }
  }

  .vs__dropdown-menu {
    min-width: 0;
  }
}
</style>
